/**
 * Easing fields of RPE events in both directions (W1-04 core-ir; DESIGN.md §6.3, §6.5, §6.11, §6.12, rpe-format.md
 * F§6.1, F§6.4-F§6.7, F§7.2-F§7.3, formats-and-packaging.md P§5.1).
 *
 * Lowering (model ease + KeySrc -> easingType, easingLeft, easingRight, bezier, bezierPoints):
 *   hold    easingType = src.easingType ?? 1, slice = src.sliceRaw ?? [0, 1], bezier 0, points src.bezierPointsRaw ?? 0
 *   tween   the tween id and slice, with src.easingType / src.sliceRaw restored while Phira resolves them to the SAME
 *           curve as the model (raw values never contradict the model, D§6.12)
 *   bezier  bezier 1, the points, easingType = src.easingType ?? 1, slice = src.sliceRaw ?? [0, 1]
 *   speed   legacy mode: src.ignoredEase written back on linear / hold keys with bezier forced to 0 (Phira ignores
 *           speed easing in that mode); never a bezier in any mode.
 * Lifting (event fields -> model ease + KeySrc) is the exact inverse; see `liftEventEase`.
 */

import { HOLD, LINEAR } from '../ease/index';
import { resolveRpe, rpeToTweenId, ZERO_BEZIER } from '../ease/rpeMap';
import type { BezierPoints, Ease, PhiraTweenDesc } from '../ease/types';
import type { IgnoredEase, KeySrc, SpeedMode } from '../model/types';

export interface EventEaseFields {
  readonly easingType: number;
  readonly easingLeft: number;
  readonly easingRight: number;
  readonly bezier: number;
  readonly bezierPoints: BezierPoints;
}

/** Plain linear fields (padding, baked pieces). */
export const LINEAR_FIELDS: EventEaseFields = Object.freeze({
  easingType: 1,
  easingLeft: 0,
  easingRight: 1,
  bezier: 0,
  bezierPoints: ZERO_BEZIER,
});

/** True for the RPE easing ids the model can hold (integers 1..29). */
export function isModelEasingId(easingType: number): boolean {
  return Number.isInteger(easingType) && easingType >= 1 && easingType <= 29;
}

export function isZeroPoints(p: BezierPoints): boolean {
  return p[0] === 0 && p[1] === 0 && p[2] === 0 && p[3] === 0;
}

function isDefaultSlice(l: number, r: number): boolean {
  return l === 0 && r === 1;
}

/** Two Phira tween descriptions resolve to the same curve (f32 slice bounds compared exactly). */
export function sameTween(a: PhiraTweenDesc, b: PhiraTweenDesc): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'static':
      return b.kind === 'static' && a.tween === b.tween;
    case 'clamped':
      return b.kind === 'clamped' && a.tween === b.tween && a.l === b.l && a.r === b.r;
    case 'bezier':
      return b.kind === 'bezier' && a.p.every((v, i) => v === b.p[i]);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Lowering
// ---------------------------------------------------------------------------------------------------------------

/** Fields of a constant event (hold segment, single key, trailing jump, materialised gap) starting at a key. */
export function constantFields(src: KeySrc | undefined): EventEaseFields {
  const slice = src?.sliceRaw;
  return {
    easingType: src?.easingType ?? 1,
    easingLeft: slice?.[0] ?? 0,
    easingRight: slice?.[1] ?? 1,
    bezier: 0,
    bezierPoints: src?.bezierPointsRaw ?? ZERO_BEZIER,
  };
}

/** Fields of a tween segment: the model's curve, with raw easingType / slice restored while equivalent. */
export function tweenFields(e: Ease & { readonly kind: 'tween' }, src: KeySrc | undefined): EventEaseFields {
  const model = resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
  const et = src?.easingType;
  const sl = src?.sliceRaw;
  const candidates: (readonly [number, number, number])[] = [];
  if (et !== undefined && sl !== undefined) candidates.push([et, sl[0], sl[1]]);
  if (et !== undefined) candidates.push([et, e.left, e.right]);
  if (sl !== undefined) candidates.push([e.id, sl[0], sl[1]]);
  let pick: readonly [number, number, number] = [e.id, e.left, e.right];
  for (const c of candidates) {
    if (sameTween(resolveRpe(c[0], c[1], c[2], 0, ZERO_BEZIER), model)) {
      pick = c;
      break;
    }
  }
  return {
    easingType: pick[0],
    easingLeft: pick[1],
    easingRight: pick[2],
    bezier: 0,
    bezierPoints: src?.bezierPointsRaw ?? ZERO_BEZIER,
  };
}

/** Fields of a bezier segment (Phira ignores easingType and the slice when bezier != 0; both kept for RPE). */
export function bezierFields(e: Ease & { readonly kind: 'bezier' }, src: KeySrc | undefined): EventEaseFields {
  const slice = src?.sliceRaw;
  return {
    easingType: src?.easingType ?? 1,
    easingLeft: slice?.[0] ?? 0,
    easingRight: slice?.[1] ?? 1,
    bezier: 1,
    bezierPoints: e.p,
  };
}

/** True for the plain linear tween (id 1, whole curve). */
export function isPlainLinear(e: Ease): boolean {
  return e.kind === 'tween' && e.id === 1 && e.left === 0 && e.right === 1;
}

/**
 * Easing fields Phira ignores on a legacy-mode speed key (src.ignoredEase), written back on linear and hold keys
 * only; never a bezier (a Phira user switching to 1.7 speed would make Phira panic): bezier 0 and, when the
 * original was a bezier, points [0, 0, 0, 0] (the original fields survive only in phiedit.json).
 */
export function ignoredEaseFields(ease: Ease, src: KeySrc | undefined): EventEaseFields | null {
  const ie = src?.ignoredEase;
  if (ie === undefined) return null;
  if (!(ease.kind === 'hold' || isPlainLinear(ease))) return null;
  return {
    easingType: ie.easingType,
    easingLeft: ie.easingLeft,
    easingRight: ie.easingRight,
    bezier: 0,
    bezierPoints: ie.bezier !== 0 ? ZERO_BEZIER : ie.bezierPoints,
  };
}

/**
 * extra.json keyframe fields (no bezier exists there; slices are used as written, unclamped). `easingLeft` /
 * `easingRight` are optional there (defaults 0 / 1): a default slice is omitted unless the keyframe was imported
 * with both keys written (then src.sliceRaw holds them, even [0, 1], and they are written back).
 */
export interface ExtraEaseFields {
  readonly easingType: number;
  readonly easingLeft?: number;
  readonly easingRight?: number;
}

function extraSlice(easingType: number, slice: readonly [number, number], explicit: boolean): ExtraEaseFields {
  if (!explicit && isDefaultSlice(slice[0], slice[1])) return { easingType };
  return { easingType, easingLeft: slice[0], easingRight: slice[1] };
}

/** Fields of a constant extra.json keyframe. */
export function extraConstantFields(src: KeySrc | undefined): ExtraEaseFields {
  const raw = src?.sliceRaw;
  return extraSlice(src?.easingType ?? 1, raw ?? [0, 1], raw !== undefined);
}

/** Fields of a tween extra.json keyframe: src.easingType while it maps to the same Phira tween; src.sliceRaw as written. */
export function extraTweenFields(e: Ease & { readonly kind: 'tween' }, src: KeySrc | undefined): ExtraEaseFields {
  const et = src?.easingType;
  const easingType = et !== undefined && rpeToTweenId(et) === rpeToTweenId(e.id) ? et : e.id;
  const raw = src?.sliceRaw;
  return extraSlice(easingType, raw ?? [e.left, e.right], raw !== undefined);
}

// ---------------------------------------------------------------------------------------------------------------
// Lifting
// ---------------------------------------------------------------------------------------------------------------

/** Mutable KeySrc under construction. */
export type MutableKeySrc = { -readonly [K in keyof KeySrc]: KeySrc[K] };

/** Raw easing fields of an event (IrEvent / IrExtraKeyframe with defaults applied). */
export interface RawEase {
  readonly easingType: number;
  readonly easingLeft: number;
  readonly easingRight: number;
  readonly bezier: number;
  readonly bezierPoints: BezierPoints;
}

/** What the lifted key's segment is. */
export interface LiftedEase {
  readonly ease: Ease;
  /** rpe170 speed step (easingType 0): the end key arrives at `start` and leaves at the raw `end`. */
  readonly step: boolean;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function ignoredOf(raw: RawEase): IgnoredEase {
  return {
    easingType: raw.easingType,
    easingLeft: raw.easingLeft,
    easingRight: raw.easingRight,
    bezier: raw.bezier,
    bezierPoints: raw.bezierPoints,
  };
}

/** Generic capture of a constant event: hold + raw easingType / slice / bezier points when not the defaults. */
function liftConstant(raw: RawEase, src: MutableKeySrc): Ease {
  if (raw.easingType !== 1) src.easingType = raw.easingType;
  if (!isDefaultSlice(raw.easingLeft, raw.easingRight)) src.sliceRaw = [raw.easingLeft, raw.easingRight];
  if (!isZeroPoints(raw.bezierPoints)) src.bezierPointsRaw = raw.bezierPoints;
  return HOLD;
}

/** Tween lift (bezier fields already handled): model id and the slice Phira applies; raw values kept in src. */
function liftTween(raw: RawEase, src: MutableKeySrc, keepPoints: boolean): Ease {
  const id = isModelEasingId(raw.easingType) ? raw.easingType : 1;
  if (!isModelEasingId(raw.easingType)) src.easingType = raw.easingType;
  const desc = resolveRpe(raw.easingType, raw.easingLeft, raw.easingRight, 0, ZERO_BEZIER);
  let left = 0;
  let right = 1;
  if (desc.kind === 'clamped') {
    left = clamp01(raw.easingLeft);
    right = clamp01(raw.easingRight);
  }
  const rl = raw.easingLeft;
  const rr = raw.easingRight;
  if (!isDefaultSlice(rl, rr) && !(rl === left && rr === right)) src.sliceRaw = [rl, rr];
  if (keepPoints && !isZeroPoints(raw.bezierPoints)) src.bezierPointsRaw = raw.bezierPoints;
  return { kind: 'tween', id, left, right };
}

/**
 * Model ease of an RPE event (DESIGN.md §6.5), writing the provenance into `src`:
 *   bezier != 0 -> Bezier (raw easingType / slice kept);  start == end -> Hold;  else the tween (raw kept).
 * Speed events follow the integrator of the speed mode (F§7.2, F§7.3, D§6.11):
 *   legacy: easing is ignored -> Linear (or Hold when constant) + src.ignoredEase for any non-plain fields;
 *   rpe170: easingType 0 -> step (Hold at start + src.rawEndValue); a bezier is never modelled on speed (kept in
 *           src.ignoredEase for phiedit.json), the remaining fields lift as for other properties.
 */
export function liftEventEase(
  raw: RawEase,
  constant: boolean,
  speed: SpeedMode | null,
  src: MutableKeySrc,
): LiftedEase {
  if (speed === 'legacy') {
    if (constant) {
      if (raw.bezier !== 0) {
        src.ignoredEase = ignoredOf(raw);
        return { ease: HOLD, step: false };
      }
      return { ease: liftConstant(raw, src), step: false };
    }
    const plain = raw.easingType === 1 && isDefaultSlice(raw.easingLeft, raw.easingRight) && raw.bezier === 0;
    if (plain) {
      if (!isZeroPoints(raw.bezierPoints)) src.bezierPointsRaw = raw.bezierPoints;
    } else {
      src.ignoredEase = ignoredOf(raw);
    }
    return { ease: LINEAR, step: false };
  }
  if (speed === 'rpe170') {
    if (raw.easingType === 0) {
      src.easingType = 0;
      if (!isDefaultSlice(raw.easingLeft, raw.easingRight)) src.sliceRaw = [raw.easingLeft, raw.easingRight];
      if (raw.bezier !== 0) src.ignoredEase = ignoredOf(raw);
      else if (!isZeroPoints(raw.bezierPoints)) src.bezierPointsRaw = raw.bezierPoints;
      return { ease: HOLD, step: !constant };
    }
    if (raw.bezier !== 0) {
      src.ignoredEase = ignoredOf(raw);
      const noBezier: RawEase = { ...raw, bezier: 0, bezierPoints: ZERO_BEZIER };
      if (constant) return { ease: liftConstant(noBezier, src), step: false };
      return { ease: liftTween(noBezier, src, false), step: false };
    }
    if (constant) return { ease: liftConstant(raw, src), step: false };
    return { ease: liftTween(raw, src, true), step: false };
  }
  if (raw.bezier !== 0) {
    if (raw.easingType !== 1) src.easingType = raw.easingType;
    if (!isDefaultSlice(raw.easingLeft, raw.easingRight)) src.sliceRaw = [raw.easingLeft, raw.easingRight];
    return { ease: { kind: 'bezier', p: raw.bezierPoints }, step: false };
  }
  if (constant) return { ease: liftConstant(raw, src), step: false };
  return { ease: liftTween(raw, src, true), step: false };
}

/**
 * Model ease of an extra.json keyframe (P§5.1): no bezier; slices are applied unclamped whenever not (0, 1), so a
 * slice the model cannot hold (outside [0, 1] or left >= right) stays in src.sliceRaw (used as written). A default
 * slice written with both keys is kept in src.sliceRaw too ([0, 1]), so the keys are written back (L1); a slice
 * written with one key only is reported by the caller (it comes back with both keys, or none when default).
 */
export function liftExtraEase(
  easingType: number,
  easingLeft: number | undefined,
  easingRight: number | undefined,
  constant: boolean,
  src: MutableKeySrc,
): Ease {
  const l = easingLeft ?? 0;
  const r = easingRight ?? 1;
  const explicitDefault = easingLeft !== undefined && easingRight !== undefined && isDefaultSlice(l, r);
  if (constant) {
    if (easingType !== 1) src.easingType = easingType;
    if (!isDefaultSlice(l, r) || explicitDefault) src.sliceRaw = [l, r];
    return HOLD;
  }
  const id = isModelEasingId(easingType) ? easingType : 1;
  if (!isModelEasingId(easingType)) src.easingType = easingType;
  if (l >= 0 && r <= 1 && l < r) {
    if (explicitDefault) src.sliceRaw = [l, r];
    return { kind: 'tween', id, left: l, right: r };
  }
  src.sliceRaw = [l, r];
  return { kind: 'tween', id, left: 0, right: 1 };
}
